import React, { useState } from 'react';

const Calendar = () => {
  // Store today's static reference for comparison
  const today = new Date();
  
  // Initialize with current date view
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isYearView, setIsYearView] = useState(false);

  const events2026 = {
    "2026-02-16": { name: "Scholarship Invite", color: "bg-amber-600 text-white" },
    "2026-03-16": { name: "1st Round Scholarship Selection", color: "bg-amber-700 text-white" },
    "2026-03-19": { name: "Eid al-Fitr", color: "bg-emerald-600 text-white" },
    "2026-03-20": { name: "Fund Raising & Eid al-Fitr", color: "bg-emerald-700 text-white" },
    "2026-04-15": { name: "Tax Filing", color: "bg-rose-600 text-white" },
    "2026-08-15": { name: "Fund Raising Event", color: "bg-emerald-700 text-white" },
    "2026-05-26": { name: "Eid al-Adha", color: "bg-emerald-600 text-white" },
    "2026-12-25": { name: "Annual Family Meeting", color: "bg-indigo-700 text-white" },
  };

  const year = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const handleMonthChange = (e) => setCurrentDate(new Date(year, parseInt(e.target.value), 1));
  const handleYearChange = (e) => setCurrentDate(new Date(parseInt(e.target.value), currentMonth, 1));

  const getFirstFriday = (y, m) => {
    let d = new Date(y, m, 1);
    while (d.getDay() !== 5) d.setDate(d.getDate() + 1);
    return d.getDate();
  };

  const isMashwaraDay = (y, m, d) => {
    const date = new Date(y, m, d);
    if (date.getDay() !== 0) return false;
    const startOf2026 = new Date(2026, 0, 4);
    const diffInMs = date - startOf2026;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays >= 0 && diffInDays % 14 === 0;
  };

  // Helper to check if a specific cell matches today
  const isToday = (y, m, d) => {
    return (
      today.getFullYear() === y &&
      today.getMonth() === m &&
      today.getDate() === d
    );
  };

  const navigate = (offset) => setCurrentDate(new Date(year, currentMonth + offset, 1));

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = Array.from({ length: 7 }, (_, i) => 2024 + i);

  // Helper to render a small month grid for Year View
  const renderMonthGrid = (mIdx) => {
    const daysInM = new Date(year, mIdx + 1, 0).getDate();
    const firstDayIdx = new Date(year, mIdx, 1).getDay();
    const firstFri = getFirstFriday(year, mIdx);

    return (
      <div key={mIdx} className="bg-white/80 p-3 rounded-2xl border border-[#D4B896]/50 shadow-sm backdrop-blur-sm">
        <h4 className="text-center font-serif font-bold text-[#2C1A14] text-xs mb-2">{months[mIdx]}</h4>
        <div className="grid grid-cols-7 gap-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
            <div key={d} className="text-[8px] text-center font-bold text-amber-900/60">{d}</div>
          ))}
          {Array.from({ length: firstDayIdx }).map((_, i) => <div key={i} />)}
          {Array.from({ length: daysInM }).map((_, i) => {
            const day = i + 1;
            const dateKey = `${year}-${String(mIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const holiday = events2026[dateKey];
            const mashwara = isMashwaraDay(year, mIdx, day);
            const fateha = day === firstFri;
            const todayMatch = isToday(year, mIdx, day);

            let bgColor = "";
            if (holiday) bgColor = holiday.color;
            else if (mashwara) bgColor = "bg-amber-200 text-amber-900 font-bold";
            else if (fateha) bgColor = "bg-sky-200 text-sky-900 font-bold";

            return (
              <div 
                key={day} 
                className={`text-[9px] aspect-square flex items-center justify-center rounded-md ${
                  bgColor ? bgColor : 'text-stone-700 hover:bg-stone-100'
                } ${todayMatch ? 'ring-2 ring-red-500 font-black shadow-sm' : ''}`}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#EBDCCD] p-3 md:p-8 flex items-center justify-center font-serif">
      <div className="w-full max-w-6xl bg-[#FFFBF7] shadow-xl rounded-3xl overflow-hidden border-2 border-[#2C1A14]">
        
        {/* HEADER / NAVIGATION */}
        <div className="p-4 md:p-8 bg-white/60 border-b border-[#D4B896]/40 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            {!isYearView ? (
              <div className="flex items-center gap-3 bg-[#EBDCCD]/50 px-4 py-2 rounded-2xl border border-[#D4B896]/40">
                <select 
                  value={currentMonth} 
                  onChange={handleMonthChange} 
                  className="bg-transparent text-xl md:text-2xl font-serif font-bold text-[#2C1A14] outline-none cursor-pointer"
                >
                  {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <span className="text-[#2C1A14]/40 font-serif font-bold">|</span>
                <select 
                  value={year} 
                  onChange={handleYearChange} 
                  className="bg-transparent text-xl md:text-2xl font-serif font-bold text-[#2C1A14] outline-none cursor-pointer"
                >
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            ) : (
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1A14]">{year} Full Calendar</h2>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button 
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 bg-[#D4B896]/30 hover:bg-[#D4B896]/50 text-[#2C1A14] text-xs font-bold uppercase tracking-wider rounded-xl transition border border-[#2C1A14]/20"
            >
              Today
            </button>
            <button 
              onClick={() => setIsYearView(!isYearView)}
              className="px-5 py-2.5 bg-[#2C1A14] text-[#EBDCCD] text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-[#3e2723] transition shadow-md border border-[#D4B896]/30"
            >
              {isYearView ? "View Month" : "View Year"}
            </button>
            {!isYearView && (
              <div className="flex gap-1 md:gap-2">
                <button onClick={() => navigate(-1)} className="p-2.5 bg-white hover:bg-[#EBDCCD]/40 rounded-xl transition border border-[#D4B896]/50 shadow-sm">
                  <svg className="w-5 h-5 text-[#2C1A14]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                </button>
                <button onClick={() => navigate(1)} className="p-2.5 bg-white hover:bg-[#EBDCCD]/40 rounded-xl transition border border-[#D4B896]/50 shadow-sm">
                  <svg className="w-5 h-5 text-[#2C1A14]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* CONTENT AREA */}
        {!isYearView ? (
          <div className="flex flex-col md:flex-row">
            {/* MONTH VIEW - LEFT */}
            <div className="flex-[2] p-4 md:p-8">
              <div className="grid grid-cols-7 gap-1.5 bg-[#EBDCCD]/30 p-2 border border-[#D4B896]/40 rounded-2xl overflow-hidden min-h-[350px] md:min-h-[480px]">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                  <div key={day} className="bg-[#2C1A14]/5 py-2 text-center text-xs font-bold text-[#2C1A14] uppercase rounded-lg h-fit">
                    {day}
                  </div>
                ))}
                {Array.from({ length: new Date(year, currentMonth, 1).getDay() }).map((_, i) => (
                  <div key={i} className="aspect-square opacity-20" />
                ))}
                {Array.from({ length: new Date(year, currentMonth + 1, 0).getDate() }).map((_, i) => {
                  const day = i + 1;
                  const dateKey = `${year}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const holiday = events2026[dateKey];
                  const mashwara = isMashwaraDay(year, currentMonth, day);
                  const fateha = day === getFirstFriday(year, currentMonth);
                  const todayMatch = isToday(year, currentMonth, day);

                  let circleColor = holiday ? holiday.color : (mashwara ? "bg-amber-200 text-amber-950" : (fateha ? "bg-sky-200 text-sky-950" : ""));
                  
                  return (
                    <div 
                      key={day} 
                      className={`bg-white/80 aspect-square rounded-xl relative flex flex-col items-center justify-center border transition-all group p-1 ${
                        todayMatch 
                          ? 'ring-2 ring-red-600 border-red-500 shadow-md z-20 scale-[1.02]' 
                          : 'border-[#D4B896]/20 hover:border-[#2C1A14]/40 hover:shadow-md'
                      }`}
                    >
                      {/* Optional Today Label Badge */}
                      {todayMatch && (
                        <span className="absolute -top-1.5 bg-red-600 text-white text-[8px] font-extrabold uppercase px-1.5 rounded-full tracking-wider z-20 shadow-sm">
                          Today
                        </span>
                      )}

                      {circleColor && (
                        <div className={`absolute inset-1 rounded-lg opacity-80 ${circleColor}`}></div>
                      )}
                      <span className={`text-sm md:text-base font-bold relative z-10 ${circleColor ? 'font-extrabold' : 'text-[#2C1A14]'}`}>
                        {day}
                      </span>
                      {circleColor && (
                        <div className="w-1.5 h-1.5 rounded-full mt-0.5 bg-[#2C1A14]/60 relative z-10"></div>
                      )}
                    </div>
                  );
                })}
                {Array.from({ length: 42 - (new Date(year, currentMonth, 1).getDay() + new Date(year, currentMonth + 1, 0).getDate()) }).map((_, i) => (
                  <div key={i} className="aspect-square opacity-20" />
                ))}
              </div>
            </div>

            {/* MONTH VIEW - RIGHT SIDEBAR */}
            <div className="flex-1 bg-[#EBDCCD]/20 border-t md:border-t-0 md:border-l border-[#D4B896]/40 p-6 md:p-8">
              <h3 className="text-lg font-serif font-bold text-[#2C1A14] mb-6 border-b border-[#D4B896]/40 pb-3 flex items-center gap-2">
                <span>🗓️</span> Upcoming Events
              </h3>
              <div className="space-y-4">
                {Object.entries(events2026).filter(([d]) => d.startsWith(`${year}-${String(currentMonth + 1).padStart(2, '0')}`)).length > 0 ? (
                  Object.entries(events2026).filter(([d]) => d.startsWith(`${year}-${String(currentMonth + 1).padStart(2, '0')}`)).map(([date, event]) => (
                    <div key={date} className="flex items-center gap-3 p-3 bg-white/70 rounded-xl border border-[#D4B896]/40 shadow-sm">
                      <div className={`w-3 h-3 rounded-full ${event.color}`}></div>
                      <div>
                        <p className="text-[10px] font-bold text-amber-900 uppercase tracking-wider">
                          {new Date(date).toLocaleDateString('default', { day: 'numeric', month: 'short' })}
                        </p>
                        <p className="text-xs font-semibold text-[#2C1A14]">{event.name}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-xs italic text-stone-500 bg-white/40 p-3 rounded-xl border border-[#D4B896]/30">
                    No scheduled special events for this month.
                  </p>
                )}

                <div className="pt-6 border-t border-[#D4B896]/40 space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#2C1A14]">Regular Schedule</p>
                  <div className="flex items-center gap-3 p-2.5 bg-sky-50/80 rounded-xl border border-sky-200">
                    <div className="w-3 h-3 rounded-full bg-sky-400"></div>
                    <span className="text-xs font-medium text-sky-950">Family Fateha (1st Friday)</span>
                  </div>
                  <div className="flex items-center gap-3 p-2.5 bg-amber-50/80 rounded-xl border border-amber-200">
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <span className="text-xs font-medium text-amber-950">Mashwara Day (Bi-weekly Sun)</span>
                  </div>
                  <div className="flex items-center gap-3 p-2.5 bg-red-50/80 rounded-xl border border-red-200">
                    <div className="w-3 h-3 rounded-full border-2 border-red-600 bg-white"></div>
                    <span className="text-xs font-medium text-red-950">Current Date</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* YEAR VIEW */
          <div className="p-4 md:p-8 bg-[#EBDCCD]/20">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {months.map((_, i) => renderMonthGrid(i))}
            </div>

            {/* YEAR LEGEND */}
            <div className="bg-white/80 p-6 rounded-2xl border border-[#D4B896]/40 shadow-sm">
              <h3 className="text-xs font-bold text-[#2C1A14] uppercase tracking-widest mb-4 pb-2 border-b border-[#D4B896]/30">
                Yearly Event Legend
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                <div className="flex items-center gap-2.5 p-2 bg-red-50 rounded-lg border border-red-200">
                  <div className="w-3 h-3 rounded border-2 border-red-600 bg-white"></div>
                  <span className="text-[11px] font-semibold text-stone-800">Current Date</span>
                </div>
                <div className="flex items-center gap-2.5 p-2 bg-sky-50 rounded-lg border border-sky-100">
                  <div className="w-3 h-3 rounded bg-sky-300"></div>
                  <span className="text-[11px] font-semibold text-stone-800">Family Fateha</span>
                </div>
                <div className="flex items-center gap-2.5 p-2 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="w-3 h-3 rounded bg-amber-300"></div>
                  <span className="text-[11px] font-semibold text-stone-800">Mashwara Day</span>
                </div>

                {Array.from(new Set(Object.values(events2026).map(e => JSON.stringify(e)))).map(eStr => {
                  const e = JSON.parse(eStr);
                  return (
                    <div key={e.name} className="flex items-center gap-2.5 p-2 bg-stone-50 rounded-lg border border-stone-200">
                      <div className={`w-3 h-3 rounded ${e.color}`}></div>
                      <span className="text-[11px] font-semibold text-stone-800">{e.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Calendar;